// authBroadcast — sincronización de sesión entre tabs.
//
// Round 2026-04-27: si el usuario cierra sesión en una tab (o expira
// vía 401), las demás tabs del mismo origin deben enterarse y limpiar
// su estado inmediatamente — sin esto, siguen mostrando UI logueada
// hasta que hagan una request que reciba 401.
//
// Usamos `BroadcastChannel` (soportado por todos los browsers modernos
// que el proyecto target). Un solo canal global "aloja-auth"; hoy solo
// hay un evento ("logout"), pero queda extensible.

const CHANNEL_NAME = "aloja-auth";

export type AuthBroadcastMessage = { type: "logout" };

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof window === "undefined") return null;
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      channel = null;
    }
  }
  return channel;
}

/** Avisa a las demás tabs que el usuario cerró sesión. */
export function broadcastLogout(): void {
  const ch = getChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type: "logout" } satisfies AuthBroadcastMessage);
  } catch {
    // canal cerrado u otro error — no es bloqueante
  }
}

/**
 * Suscribe un handler a los eventos del canal.
 * Devuelve la función `unsubscribe` para limpiar.
 */
export function subscribeAuthEvents(
  handler: (msg: AuthBroadcastMessage) => void,
): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (e: MessageEvent<AuthBroadcastMessage>) => {
    if (e.data && typeof e.data === "object" && "type" in e.data) {
      handler(e.data);
    }
  };
  ch.addEventListener("message", listener);
  return () => {
    ch.removeEventListener("message", listener);
  };
}
